import { useEffect, useRef, useState } from "react";
import { vibrate } from "../hooks.ts";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import { Kostka } from "./Kostka.tsx";

/**
 * Kości — ekran tury i tabela wyników.
 *
 * Sercem jest rząd pięciu kości, które klika się, żeby zatrzymać. Wybór
 * kategorii pokazuje od razu, ile dałaby punktów, bo liczenie w pamięci przy
 * trzynastu kratkach jest dokładnie tą częścią gry, którą warto zdjąć z głowy.
 */

export interface KosciView {
  phase: "lobby" | "tura" | "finished";
  roundNo: number;
  roundCount: number;
  hostId: string;
  players: string[];
  currentPlayerId: string | null;
  mojaTura: boolean;
  dice: number[];
  held: boolean[];
  rollsLeft: number;
  categories: Array<{ key: string; label: string; hint: string }>;
  scores: Record<string, Record<string, number>>;
  preview: Record<string, number>;
  totals: Record<
    string,
    { upper: number; upperBonus: number; lower: number; generalBonus: number; total: number }
  >;
  upperThreshold: number;
  postepGory: Record<
    string,
    { suma: number; brakuje: number; zapas: number; zdobyty: boolean; przepadl: boolean }
  >;
  canStart: boolean;
}

/**
 * Animacja rzutu.
 *
 * Kości niezatrzymane podskakują i przez chwilę migają losowymi ściankami.
 * Bez tego wynik podmienia się w miejscu i przy trzech rzutach pod rząd nie
 * widać, czy cokolwiek się wydarzyło.
 *
 * Wyzwalaczem jest **licznik pozostałych rzutów**, a nie zmiana wartości kości.
 * To istotne: przy piątce, która wypadła drugi raz z rzędu, porównywanie
 * wartości uznawało, że nic się nie stało — i akurat ta kość zostawała
 * nieruchoma, co wyglądało na zawieszenie gry.
 *
 * Zatrzymane kości leżą nieruchomo celowo — to potwierdzenie, że zatrzymanie
 * zadziałało.
 */
function useAnimacjaRzutu(
  dice: number[],
  held: boolean[],
  rollsLeft: number,
): { rzucane: Set<number>; pokazywane: number[] } {
  const [rzucane, setRzucane] = useState<Set<number>>(new Set());
  const [pokazywane, setPokazywane] = useState<number[]>(dice);
  const poprzednieRzuty = useRef<number>(rollsLeft);

  useEffect(() => {
    const byloRzucone = rollsLeft < poprzednieRzuty.current;
    poprzednieRzuty.current = rollsLeft;

    if (!byloRzucone || dice.length === 0) {
      setPokazywane(dice);
      setRzucane(new Set());
      return;
    }

    const wRuchu = new Set(
      dice.map((_, index) => index).filter((index) => !(held[index] ?? false)),
    );
    if (wRuchu.size === 0) {
      setPokazywane(dice);
      return;
    }

    setRzucane(wRuchu);
    vibrate(30);

    let krok = 0;
    const timer = setInterval(() => {
      krok += 1;
      if (krok >= 7) {
        clearInterval(timer);
        setPokazywane(dice);
        setRzucane(new Set());
        return;
      }
      setPokazywane(
        dice.map((wartosc, index) =>
          wRuchu.has(index) ? 1 + Math.floor(Math.random() * 6) : wartosc,
        ),
      );
    }, 70);

    return () => clearInterval(timer);
    // Zależność po liczniku rzutów: serwer przysyła nowy widok także przy
    // zmianach, które kości nie dotyczą, a te nie mają nic animować.
  }, [rollsLeft]);

  return { rzucane, pokazywane };
}

export function Kosci({
  view,
  nicki,
  mojeId,
  onStart,
  onRzut,
  onZatrzymaj,
  onZapisz,
}: {
  view: KosciView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onRzut: () => void;
  onZatrzymaj: (index: number) => void;
  onZapisz: (category: string) => void;
}) {
  const { rzucane, pokazywane } = useAnimacjaRzutu(view.dice, view.held, view.rollsLeft);

  if (view.phase === "lobby") {
    return (
      <>
        <Karta>
          <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
          <div className="space-y-1">
            {view.players.map((id) => (
              <div key={id} className="flex justify-between">
                <span>{nicki[id] ?? id}</span>
                {id === view.hostId ? <Odznaka>host</Odznaka> : null}
              </div>
            ))}
          </div>
        </Karta>
        <Komunikat>
          Pięć kości, trzy rzuty na turę, trzynaście kategorii. Kliknij kość, żeby ją zatrzymać
          przed kolejnym rzutem.
        </Komunikat>
        {view.canStart ? (
          <Przycisk wariant="glowny" pelna onClick={onStart}>
            Zaczynamy
          </Przycisk>
        ) : (
          <Komunikat>Czekamy, aż host zacznie partię.</Komunikat>
        )}
      </>
    );
  }

  const koniec = view.phase === "finished";
  const kolejnosc = [...view.players].sort(
    (a, b) => (view.totals[b]?.total ?? 0) - (view.totals[a]?.total ?? 0),
  );

  return (
    <>
      {koniec ? (
        <Karta>
          <div className="text-sm text-tekst-slaby mb-3">Wynik końcowy</div>
          <div className="space-y-2">
            {kolejnosc.map((id, miejsce) => (
              <div key={id} className="flex items-center gap-3">
                <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
                <span className="flex-1 truncate">
                  {nicki[id] ?? id}
                  {miejsce === 0 ? " 🏆" : ""}
                </span>
                <span className="text-xl font-semibold tabular">{view.totals[id]?.total ?? 0}</span>
              </div>
            ))}
          </div>
        </Karta>
      ) : (
        <>
          <div className="text-center">
            {view.mojaTura ? (
              <div className="text-akcent text-lg">Twoja tura</div>
            ) : (
              <div className="text-tekst-slaby">
                Tura: {nicki[view.currentPlayerId ?? ""] ?? "?"}
              </div>
            )}
          </div>

          {/*
            * Kości i przycisk rzutu przyklejone do góry.
            *
            * Tabela ma trzynaście (a z parami piętnaście) wierszy i nie mieści
            * się w kadrze telefonu — przewijając ją do wolnej kategorii,
            * traciło się z oczu to, co się właśnie wyrzuciło, i trzeba było
            * skakać w górę i w dół. Tło musi być kryjące, bo wiersze
            * przejeżdżają pod spodem; ujemne marginesy rozciągają je na całą
            * szerokość ekranu mimo paddingu ramki.
            */}
          <div className="sticky top-0 z-10 -mx-4 border-b border-obrys bg-tlo px-4 pb-2">
          <div className="grid grid-cols-5 gap-2 justify-items-center py-2">
            {(view.dice.length > 0 ? pokazywane : [0, 0, 0, 0, 0]).map((wartosc, index) => (
              <Kostka
                key={index}
                wartosc={wartosc}
                zatrzymana={view.held[index] ?? false}
                rzucana={rzucane.has(index)}
                disabled={!view.mojaTura || view.dice.length === 0 || view.rollsLeft === 0}
                onClick={() => onZatrzymaj(index)}
              />
            ))}
          </div>

          {view.mojaTura ? (
            <Przycisk
              wariant="glowny"
              pelna
              onClick={onRzut}
              disabled={view.rollsLeft === 0}
              className="min-h-16 text-xl"
            >
              {view.rollsLeft === 3
                ? "Rzuć"
                : view.rollsLeft > 0
                  ? `Rzuć ponownie (${view.rollsLeft})`
                  : "Wybierz kategorię"}
            </Przycisk>
          ) : null}
          </div>
        </>
      )}

      <Tabela
        view={view}
        nicki={nicki}
        mojeId={mojeId}
        onZapisz={onZapisz}
        interaktywna={!koniec && view.mojaTura && view.dice.length > 0}
      />
    </>
  );
}

function Tabela({
  view,
  nicki,
  mojeId,
  onZapisz,
  interaktywna,
}: {
  view: KosciView;
  nicki: Record<string, string>;
  mojeId: string;
  onZapisz: (category: string) => void;
  interaktywna: boolean;
}) {
  const moje = view.scores[mojeId] ?? {};

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-2">
        Runda {Math.min(view.roundNo, view.roundCount)}/{view.roundCount}
      </div>

      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-tekst-slaby">
              <th className="text-left font-normal pb-2">Kategoria</th>
              {view.players.map((id) => (
                <th key={id} className="text-right font-normal pb-2 px-1 max-w-16 truncate">
                  {nicki[id] ?? id}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/*
              Suma nad kategoriami, nie pod nimi.
              Tabela ma trzynaście wierszy i na telefonie nie mieści się w kadrze
              — wynik na dole trzeba było przewijać, a jest tym, co sprawdza się
              najczęściej.
            */}
            <tr className="font-semibold">
              <td className="py-2">Razem</td>
              {view.players.map((id) => (
                <td key={id} className="text-right px-1 tabular">
                  {view.totals[id]?.total ?? 0}
                </td>
              ))}
            </tr>

            {view.categories.map((kategoria) => {
              const wolna = !(kategoria.key in moje);
              const podpowiedz = view.preview[kategoria.key];
              const doKliknięcia = interaktywna && wolna && podpowiedz !== undefined;

              return (
                <tr key={kategoria.key} className="border-t border-obrys">
                  <td className="py-2 pr-2">
                    {/*
                      Nazwa i podpowiedź w jednej linii: trzynaście kategorii
                      w dwóch wierszach każda robiło z tabeli ścianę tekstu.
                      Podpowiedź mocno przygaszona — jest dla tego, kto czyta
                      pierwszy raz, a nie dla kogoś, kto zna zasady.
                    */}
                    {doKliknięcia ? (
                      <button
                        onClick={() => onZapisz(kategoria.key)}
                        className="text-left w-full active:text-akcent"
                      >
                        <span>{kategoria.label}</span>{" "}
                        <span className="text-xs text-tekst/30">{kategoria.hint}</span>
                      </button>
                    ) : (
                      <>
                        <span>{kategoria.label}</span>{" "}
                        <span className="text-xs text-tekst/30">{kategoria.hint}</span>
                      </>
                    )}
                  </td>
                  {view.players.map((id) => {
                    const zapisane = view.scores[id]?.[kategoria.key];
                    const mojaPodpowiedz = id === mojeId && doKliknięcia;
                    return (
                      <td key={id} className="text-right px-1 tabular">
                        {zapisane !== undefined ? (
                          <span>{zapisane}</span>
                        ) : mojaPodpowiedz ? (
                          // Podpowiedź jest wyszarzona, żeby nie mylić jej
                          // z punktami już zapisanymi.
                          <button
                            onClick={() => onZapisz(kategoria.key)}
                            className="text-akcent/70 active:text-akcent"
                          >
                            +{podpowiedz}
                          </button>
                        ) : (
                          <span className="text-tekst-slaby">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            <tr className="border-t border-obrys">
              <td className="py-2 text-tekst-slaby">
                Bonus za górę
                <div className="text-xs">od {view.upperThreshold} pkt</div>
              </td>
              {view.players.map((id) => (
                <td key={id} className="text-right px-1 tabular text-tekst-slaby">
                  <PostepBonusu postep={view.postepGory[id]} bonus={view.totals[id]?.upperBonus} />
                </td>
              ))}
            </tr>

          </tbody>
        </table>
      </div>
    </Karta>
  );
}

/**
 * Stan bonusu za górną część w jednej kratce tabeli.
 *
 * Samo „+35 albo —" nie mówiło nic o tym, czy do bonusu jeszcze da się dojść.
 * Stąd dwie liczby: ile brakuje do progu oraz zapas względem „po trzy sztuki"
 * w kategoriach już zapisanych — bo to zapas, a nie odległość od progu,
 * odpowiada na pytanie „czy idę dobrze".
 */
function PostepBonusu({
  postep,
  bonus,
}: {
  postep?: { suma: number; brakuje: number; zapas: number; zdobyty: boolean; przepadl: boolean };
  bonus?: number;
}) {
  if (!postep) return <span>—</span>;
  if (postep.zdobyty) return <span className="text-akcent">+{bonus ?? 0}</span>;
  if (postep.przepadl) return <span>przepadł</span>;

  const zapas = postep.zapas > 0 ? `+${postep.zapas}` : String(postep.zapas);
  return (
    <span className="block leading-tight">
      <span className="block">brakuje {postep.brakuje}</span>
      <span className={postep.zapas < 0 ? "block text-xs text-blad" : "block text-xs text-akcent"}>
        {zapas} do „po trzy"
      </span>
    </span>
  );
}
